import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";
import { XIcon } from "@heroicons/react/solid";
import moment from "moment";
import { ChangeEvent, HTMLAttributes, forwardRef } from "react";
import DatePicker from "react-datepicker";

import Button, { ButtonProps, buttonTheme } from "@components/button/button";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import {
  classNames,
  inputThemeDefaultClassName,
  inputThemeTextClassName,
} from "@helpers/css";
import { removeCurrentYear } from "@helpers/helpers";

import { InputTheme } from "./input";

const InputDatePickerCustomHeaderWithClearButton = ({
  value,
  onChangeDate,
  clearable,
  currentDate,
  decreaseMonth,
  increaseMonth,
  prevMonthButtonDisabled,
  nextMonthButtonDisabled,
}: {
  value: Date | null | undefined | string;
  onChangeDate: (date: Date | null) => void;
  clearable: boolean;
  currentDate: Date;
  decreaseMonth: () => void;
  increaseMonth: () => void;
  prevMonthButtonDisabled: boolean;
  nextMonthButtonDisabled: boolean;
}) => {
  const handleClickClearDate = () => {
    onChangeDate(null);
  };
  return (
    <div className="flex justify-between items-center px-2 py-1">
      <button
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
        className="py-0.5 px-1 text-gray-600 rounded hover:bg-black/5 hover:text-gray-800"
      >
        <ChevronLeftIcon className="h-5 w-5 shrink-0" />
      </button>
      <div className="font-medium text-sm flex gap-1.5 items-center">
        {moment(currentDate).format("MMMM YYYY")}
        {value && clearable && (
          <Tooltip text="Clear selected date">
            <button
              className="text-gray-400 p-1 rounded-full hover:bg-black/5 hover:text-gray-800"
              onClick={handleClickClearDate}
              aria-label="Clear date button"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </Tooltip>
        )}
      </div>
      <button
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
        className="py-0.5 px-1 text-gray-600 rounded hover:bg-black/5 hover:text-gray-800"
      >
        <ChevronRightIcon className="h-5 w-5 shrink-0" />
      </button>
    </div>
  );
};

const InputDatePickerIcon = ({ className }: { className?: string }) => {
  return (
    <TopicflowIcon
      type="meeting"
      className={classNames("text-gray-400 h-4 w-4", className)}
    />
  );
};

type InputDatePickerButtonProps = Pick<
  ButtonProps,
  | "theme"
  | "leftNegativeMargin"
  | "rounded"
  | "showDownChevronIcon"
  | "size"
  | "tooltip"
>;

const CustomDatePickerButton = forwardRef<
  HTMLButtonElement,
  {
    value?: string;
    placeholder?: string;
    onClick?: () => void;
    disabled?: boolean;
    buttonProps: InputDatePickerButtonProps;
    showIcon: boolean;
  }
>(
  (
    {
      value,
      onClick,
      disabled,
      placeholder = "No date",
      buttonProps,
      showIcon = true,
    },
    ref
  ) => {
    const label = removeCurrentYear(value || "");
    return (
      <Button
        onClick={onClick}
        ref={ref}
        theme={buttonTheme.text}
        disabled={disabled}
        {...buttonProps}
      >
        {showIcon && <InputDatePickerIcon />}
        <span className={classNames("empty:hidden", !label && "opacity-50")}>
          {label || placeholder}
        </span>
      </Button>
    );
  }
);

const CustomDatePickerInput = forwardRef<
  HTMLInputElement,
  {
    value?: string;
    isClearable?: boolean;
    onClick?: () => void;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  } & HTMLAttributes<HTMLInputElement>
>(({ isClearable, value, onChange, ...htmlProps }, ref) => {
  const showClearButton = value && isClearable;
  return (
    <>
      <input
        value={value}
        onChange={onChange}
        {...htmlProps}
        className={classNames(
          htmlProps.className,
          isClearable && "!pr-[34px]" // we don't do it depending on whether there is a value or not otherwise the width changes.
        )}
      />
      {showClearButton && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onChange?.({
              target: { value: "" } as HTMLInputElement,
            } as ChangeEvent<HTMLInputElement>);
          }}
          className="absolute top-1/2 -translate-y-1/2 right-2 p-1 rounded-md hover:bg-gray-100 text-gray-400 hover:text-gray-500"
        >
          <TopicflowIcon type="close" className="h-3 w-3" />
        </button>
      )}
    </>
  );
});

const InputDatePicker = ({
  disabled,
  date,
  onChangeDate,
  minDate = null,
  maxDate = null,
  isClearable = true,
  placeholderText = "No date",
  className,
  dateFormat = "MMM d, yyyy",
  withPortal = false,
  showTimeInput = false,
  ariaLabelledBy,
  wrapperClassName,
  theme = InputTheme.default,
  showIcon = true,
  timeIntervals = 15,
  showTimeSelect = false,
  showTimeSelectOnly = false,
  buttonProps,
}: {
  disabled?: boolean;
  date?: Date | string | null;
  onChangeDate: (date: Date | null) => void;
  minDate?: any;
  maxDate?: any;
  isClearable?: boolean;
  placeholderText?: string;
  className?: any;
  dateFormat?: string;
  withPortal?: boolean;
  showTimeInput?: boolean;
  ariaLabelledBy?: string;
  wrapperClassName?: string;
  theme?: InputTheme;
  showIcon?: boolean;
  timeIntervals?: number;
  showTimeSelect?: boolean;
  showTimeSelectOnly?: boolean;
  buttonProps?: InputDatePickerButtonProps;
}) => {
  return (
    <DatePicker
      selected={date ? moment(date).toDate() : null}
      dateFormat={dateFormat}
      placeholderText={placeholderText}
      popperClassName="z-tooltip"
      onChange={onChangeDate}
      portalId="portal-react-datepicker"
      popperProps={{ strategy: "fixed" }}
      minDate={minDate}
      disabled={disabled}
      maxDate={maxDate}
      className={classNames(
        theme === InputTheme.default && inputThemeDefaultClassName,
        theme === InputTheme.text && inputThemeTextClassName,
        className
      )}
      wrapperClassName={wrapperClassName}
      showIcon={buttonProps === undefined && showIcon}
      icon={<InputDatePickerIcon />}
      ariaLabelledBy={ariaLabelledBy}
      withPortal={withPortal}
      showTimeInput={showTimeInput}
      timeInputLabel="Time:"
      data-lpignore="true"
      autoComplete="off"
      timeIntervals={timeIntervals}
      showTimeSelect={showTimeSelect}
      showTimeSelectOnly={showTimeSelectOnly}
      renderCustomHeader={({
        date: currentDate,
        decreaseMonth,
        increaseMonth,
        prevMonthButtonDisabled,
        nextMonthButtonDisabled,
      }) => (
        <InputDatePickerCustomHeaderWithClearButton
          value={date}
          onChangeDate={onChangeDate}
          clearable={isClearable}
          currentDate={currentDate}
          decreaseMonth={decreaseMonth}
          increaseMonth={increaseMonth}
          prevMonthButtonDisabled={prevMonthButtonDisabled}
          nextMonthButtonDisabled={nextMonthButtonDisabled}
        />
      )}
      customInput={
        buttonProps === undefined ? (
          <CustomDatePickerInput isClearable={isClearable} />
        ) : (
          <CustomDatePickerButton
            buttonProps={buttonProps}
            showIcon={showIcon}
          />
        )
      }
    />
  );
};

export default InputDatePicker;
